import {
    DownOutlined,
    LoadingOutlined,
    UpOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Input, theme } from 'antd';
import { observer } from 'mobx-react';
import {
    type ChangeEvent as ReactChangeEvent,
    type KeyboardEvent as ReactKeyboardEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import type CommentStore from 'stores/Comment';
import type CommentsManager from 'stores/CommentsManager';
import styled, { css } from 'styled-components';
import { textareaHeight } from 'utils/Textarea';

import Comment from './Comment';
import { type Commentable, type UserLike } from './types';

// See the note in components/Views/Animation/Drawers/DrawerHeader.tsx for why
// these are read at module scope, and what that means for a future dark mode.
const { colorInfoText } = theme.getDesignToken();

export type ComponentVariant = 'floating' | 'panel';

interface ContainerProps {
    $variant: ComponentVariant;
    $isCollapsed?: boolean;
    $isPanelOpen?: boolean;
}

const Container = styled.div<ContainerProps>`
    /* shared */
    background: white;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.7, 0.3, 0.1, 1);

    height: ${(p) =>
        p.$variant === 'floating'
            ? p.$isCollapsed
                ? '44px'
                : '400px'
            : '100%'};

    right: ${(p) =>
        p.$variant === 'floating'
            ? p.$isPanelOpen
                ? '730px'
                : '10px'
            : 'initial'};

    width: ${(p) => (p.$variant === 'floating' ? '400px' : '100%')};

    ${(p) =>
        p.$variant === 'floating'
            ? css`
                  border-radius: 5px;
                  border: 1px solid #ddd;
                  bottom: 10px;
                  position: absolute;
              `
            : css`
                  display: flex;
                  flex-flow: column;
              `}
`;

interface VariantProps {
    $variant: ComponentVariant;
}

const InnerContainer = styled.div<VariantProps>`
    display: flex;
    flex-wrap: wrap;
    flex-flow: column;
    color: #123;

    ${(p) =>
        p.$variant === 'floating'
            ? css`
                  height: 400px;
                  width: 400px;
              `
            : css`
                  width: 100%;
                  height: 100%;
              `}
`;

const NoCommentsPlaceholder = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    color: #ddd;
    height: 100%;
    width: 100%;
`;

const UnauthenticatedPlaceholder = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-flow: column;
    flex-grow: 1;
    color: #ddd;
    width: 100%;
`;

const CommentsContainer = styled.div<VariantProps>`
    display: flex;
    overflow-y: auto;
    flex-grow: 1;
    flex-flow: column;
    background: #f9f9f9;
    flex-shrink: 0;
    flex-basis: 0;

    max-height: ${(p) => (p.$variant === 'floating' ? '318px' : '100%')};
`;

interface CommentInputProps {
    $isCollapsed?: boolean;
}

const CommentInput = styled.textarea<CommentInputProps>`
    height: 40px;
    border: 0;
    border-top: 1px solid #ddd;
    border-radius: 0 0 5px 5px;
    padding: 8px 11px;
    resize: none;
    display: flex;
    font-size: 14px;
    outline: 0;

    ${(p) => p.$isCollapsed && `overflow-y: hidden;`}

    &::placeholder {
        opacity: 0.5;
    }

    &:disabled {
        background: #f9f9f9;
    }

    &:focus {
        outline-width: 0;
    }
`;

type CommentState = 'default' | 'enter-placeholder';

const Title = styled.div`
    font-weight: bold;
    border-bottom: 1px solid #ddd;
    padding: 14px;
    height: 45px;
    display: flex;
    align-items: center;
    flex-flow: row;
    cursor: pointer;
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
    transition: background-color 0.1s;
`;

interface TitleTextProps {
    $showUnreadIndicator?: boolean;
}

const TitleText = styled.span<TitleTextProps>`
    position: relative;
    text-align: left;
    flex-grow: 1;

    &::before {
        display: ${(p) => (p.$showUnreadIndicator ? 'block' : 'none')};
        position: absolute;
        left: -0.5em;
        top: -0.5em;
        content: '●';
        color: ${colorInfoText};
    }
`;

function determineTitleContent(
    comments: CommentsManager,
    isCollapsed: boolean
) {
    if (comments.loading) return <LoadingOutlined />;
    if (!isCollapsed) return 'Comments';
    if (comments.loaded) return `Comments (${comments.all.length})`;
    return '';
}

export interface CommentsProps {
    isPanelOpen?: boolean;
    parentDoc: Commentable;
    user: UserLike;
    variant: ComponentVariant;
}

function renderComments(
    user: UserLike,
    isLoading: boolean,
    comments: ReadonlyArray<CommentStore>,
    onAnonymousNameSubmit: (name: string) => void
) {
    if (!user.authenticated && !user.placeholderName) {
        return (
            <UnauthenticatedPlaceholder>
                <UserOutlined style={{ fontSize: '40px' }} />
                <p style={{ color: '#ccc' }}>
                    Enter your name to begin commenting.
                </p>
                <Input
                    onPressEnter={(e) =>
                        onAnonymousNameSubmit(e.currentTarget.value)
                    }
                    style={{ width: '80%' }}
                    placeholder={'Your name'}
                />
            </UnauthenticatedPlaceholder>
        );
    }

    if (isLoading) {
        return (
            <NoCommentsPlaceholder>
                <LoadingOutlined style={{ fontSize: '40px' }} />
            </NoCommentsPlaceholder>
        );
    }

    if (comments.length === 0) {
        return <NoCommentsPlaceholder>No comments</NoCommentsPlaceholder>;
    }

    return (
        <>
            {comments.map(
                (comment) =>
                    comment && (
                        <Comment
                            key={comment.id}
                            comment={comment}
                            user={user}
                        />
                    )
            )}
        </>
    );
}

function Comments({ isPanelOpen, parentDoc, user, variant }: CommentsProps) {
    const commentsContainerRef = useRef<HTMLDivElement>(null);
    const scrollBottomLock = useRef(true);

    const [collapsed, setCollapsed] = useState(variant === 'floating');
    const [displayState, setDisplayState] = useState<CommentState>(
        user.authenticated ? 'default' : 'enter-placeholder'
    );

    const isRead = parentDoc.isRead ?? true;

    const titleContent =
        variant === 'floating'
            ? determineTitleContent(parentDoc.comments, collapsed)
            : null;

    const handlePlaceholderName = (name: string) => {
        setDisplayState('default');
        user.setPlaceholderName(name);
        // Re-pin only. Swapping the placeholder for the comment list is a
        // render, so the layout effect does the scrolling once it lands.
        scrollBottomLock.current = true;
    };

    // Method to adjust the height of a textarea element
    const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
        textarea.style.height = textareaHeight(textarea.value, { maxLines: 4 });

        // The one place that still scrolls directly. This writes style outside
        // React, so no render follows and the layout effect will not fire -
        // but a taller textarea shrinks CommentsContainer (flex-grow inside a
        // fixed-height column) and pushes the newest comment out of view.
        scrollToBottom();
    };

    // Method to handle changes in the textarea
    const handleChange = (e: ReactChangeEvent<HTMLTextAreaElement>) => {
        adjustTextareaHeight(e.target);
    };

    // Method to handle the Enter key press events
    const handleCommentKeyDown = (
        e: ReactKeyboardEvent<HTMLTextAreaElement>
    ) => {
        // This handler is only interested in the enter key, when shift is
        // not held. If shift is held, default event handling will cause a
        // newline to be added.
        if (e.shiftKey || e.key !== 'Enter') {
            return;
        }

        const textarea = e.currentTarget;
        const comment = textarea.value.trim();

        if (comment) {
            // Submit the comment if it's not empty
            void parentDoc.comments.addComment(comment);
            // Re-pin only. addComment is a Firestore round trip, so the
            // comment reaches the DOM some renders later - the layout effect
            // scrolls to it when it does.
            scrollBottomLock.current = true;
            textarea.value = '';
            e.preventDefault();
        }
        adjustTextareaHeight(textarea);
    };

    const setIsViewing = (isViewing: boolean) => {
        parentDoc.setViewingComments(isViewing);
    };

    const handleCollapse = () => {
        const newCollapsedValue = !collapsed;
        setCollapsed(newCollapsedValue);
        setIsViewing(!newCollapsedValue);
    };

    // Synchronous, and called from a layout effect rather than by whoever
    // changed the content. It used to await a fixed 150ms in the hope that
    // React had committed by then, which raced a Firestore round trip on the
    // submit path. Nothing here needs to wait: the effect below already runs
    // after the commit, and the one direct caller mutates the DOM itself.
    const scrollToBottom = useCallback(() => {
        if (scrollBottomLock.current && commentsContainerRef.current) {
            commentsContainerRef.current.scrollTop =
                commentsContainerRef.current.scrollHeight;
        }
    }, []);

    const checkScroll = () => {
        const commentsContainer = commentsContainerRef.current;
        if (!commentsContainer) return;
        scrollBottomLock.current =
            commentsContainer.offsetHeight + commentsContainer.scrollTop ===
            commentsContainer.scrollHeight;
    };

    useEffect(() => {
        const refreshDatetime = () => {
            if (parentDoc.comments.loaded) {
                parentDoc.comments.updateCommentsDatetime();
            }
        };

        const intervalHnd = window.setInterval(refreshDatetime, 60 * 1000);

        return () => {
            window.clearInterval(intervalHnd);
        };
    }, [parentDoc.comments]);

    // Keep the newest comment in view as content arrives, keyed on what is
    // actually rendered rather than called by whoever changed it.
    //
    // This replaces a DocumentCollectionEvents.LOADED listener, which only
    // ever covered the first load - DocumentCollection.load dispatched it
    // under `if (!this.mounted)`, and later snapshots go through onUpdate
    // without dispatching. So comments arriving afterwards, yours once the
    // write round-trips or anyone else's at any time, had no event to hang
    // a scroll off. A 10ms setInterval used to paper over that.
    //
    // useLayoutEffect rather than useEffect so the scroll is applied before
    // paint, with no flash at the previous position.
    useLayoutEffect(() => {
        scrollToBottom();
    }, [parentDoc.comments.all.length, displayState, scrollToBottom]);

    const isDisplaced = variant === 'floating' && isPanelOpen;

    return (
        <Container
            $variant={variant}
            $isCollapsed={collapsed}
            $isPanelOpen={isDisplaced}
        >
            <InnerContainer
                $variant={variant}
                onClick={() => parentDoc.updateViewHistory?.()}
            >
                {variant === 'floating' && (
                    <Title onClick={handleCollapse}>
                        <TitleText $showUnreadIndicator={!isRead}>
                            {titleContent}
                        </TitleText>
                        {collapsed ? <UpOutlined /> : <DownOutlined />}
                    </Title>
                )}
                <CommentsContainer
                    $variant={variant}
                    ref={commentsContainerRef}
                    onScroll={checkScroll}
                >
                    {renderComments(
                        user,
                        parentDoc.comments.loading,
                        parentDoc.comments.all,
                        handlePlaceholderName
                    )}
                </CommentsContainer>
                <CommentInput
                    $isCollapsed={collapsed}
                    onChange={handleChange}
                    onKeyDown={handleCommentKeyDown}
                    onFocus={() => {
                        setIsViewing(true);
                    }}
                    onBlur={() => {
                        setIsViewing(false);
                    }}
                    disabled={displayState === 'enter-placeholder'}
                    placeholder={
                        displayState === 'enter-placeholder'
                            ? ''
                            : 'Write a comment'
                    }
                />
            </InnerContainer>
        </Container>
    );
}

export default observer(Comments);
